import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";
import * as serviceWorkerRegistration from '@/utils/serviceWorkerRegistration';
import { checkVersion } from '@/utils/versionCheck';
import '@/utils/debug'; // Initialize debug utilities

// Global axios safeguard — every request to our backend should carry
// the httpOnly auth cookies. Setting this once at app boot prevents any
// future call from accidentally omitting `withCredentials` and falling
// back to the (now-removed) Bearer-token pattern. The frontend makes no
// 3rd-party axios calls, so a global default is safe.
axios.defaults.withCredentials = true;

// ---------------------------------------------------------------------------
// Cross-origin auth persistence (2026-05-24 — production launch blocker fix)
//
// Why this exists:
//   In preview (same-origin) cookies work fine. In production, frontend lives
//   on https://healthloop.app and backend lives on a different origin. Modern
//   browsers (Safari ITP, Firefox TCP, Chrome 3rd-party cookie phaseout)
//   partition or evict cross-site cookies aggressively — even with
//   SameSite=None + Secure correctly configured — so users get logged out
//   after browser-close, idle timers, or tracker-prevention sweeps.
//
// The fix is a localStorage Bearer fallback that runs alongside cookies:
//   - Login/register/refresh responses now include `{ tokens: { access_token,
//     refresh_token } }` in the body. We persist them to localStorage.
//   - Every outbound axios call gets `Authorization: Bearer <access_token>`
//     attached as a belt-and-suspenders backup to the cookie.
//   - On 401, the silent-refresh interceptor sends the refresh token via the
//     `Authorization: Bearer <refresh>` header so refresh works even when
//     the refresh_token cookie has been evicted.
//   - On logout, we clear localStorage in addition to the cookies.
//
// Trade-off: localStorage tokens are JS-accessible, so XSS could exfiltrate
// them. We already harden against XSS at the React layer (no innerHTML on
// user content), and httpOnly cookies remain the primary token carrier when
// the browser doesn't block them. For an MVP wellness app this trade-off is
// acceptable — the alternative is "users can't stay logged in", which is a
// strictly worse failure mode.
// ---------------------------------------------------------------------------
const LS_ACCESS = 'healthloop_access_token';
const LS_REFRESH = 'healthloop_refresh_token';

const __readToken = (key) => {
  try { return localStorage.getItem(key) || null; } catch (_) { return null; }
};
const __writeToken = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch (_) { /* private mode / disabled storage — silently degrade */ }
};

// Exposed for AuthContext to call after login/register/refresh/logout.
// We attach to `window` so any module can use it without import cycles.
if (typeof window !== 'undefined') {
  window.__hlAuth = {
    storeTokens: (tokens) => {
      if (!tokens) return;
      __writeToken(LS_ACCESS, tokens.access_token);
      __writeToken(LS_REFRESH, tokens.refresh_token);
    },
    clearTokens: () => {
      __writeToken(LS_ACCESS, null);
      __writeToken(LS_REFRESH, null);
    },
    getAccess: () => __readToken(LS_ACCESS),
    getRefresh: () => __readToken(LS_REFRESH),
  };
}

// REQUEST interceptor — attach Bearer header from localStorage if present.
// We only attach for calls to our own backend (matched by REACT_APP_BACKEND_URL
// or relative '/api/' paths) so 3rd-party calls (if ever added) don't leak
// our token.
axios.interceptors.request.use((config) => {
  try {
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const url = config.url || '';
    const isOurs =
      url.startsWith(backend) || url.startsWith('/api/') || url.startsWith('/api');
    if (!isOurs) return config;
    const access = __readToken(LS_ACCESS);
    if (access) {
      config.headers = config.headers || {};
      // Don't override if caller has already set one explicitly.
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${access}`;
      }
    }
  } catch (_) { /* never block request on interceptor error */ }
  return config;
});

// RESPONSE interceptor — single-flight silent refresh on 401.
let __refreshInFlight = null;
const __performGlobalRefresh = () => {
  if (!__refreshInFlight) {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const headers = {};
    const lsRefresh = __readToken(LS_REFRESH);
    // Send refresh via Bearer header as the cross-origin fallback. The
    // cookie still gets sent automatically (when not evicted); backend
    // accepts either.
    if (lsRefresh) headers.Authorization = `Bearer ${lsRefresh}`;
    __refreshInFlight = axios
      .post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true, headers })
      .then((res) => {
        // Persist newly-issued tokens immediately so the retried original
        // request picks them up via the REQUEST interceptor.
        if (res?.data?.tokens) {
          window.__hlAuth?.storeTokens?.(res.data.tokens);
        }
        return res;
      })
      .finally(() => {
        // Release on next tick so coincident 401s queue on the same
        // promise but later 401s start a fresh attempt.
        setTimeout(() => { __refreshInFlight = null; }, 0);
      });
  }
  return __refreshInFlight;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Network failures (true offline / DNS / timeout) MUST NOT trigger a
    // logout flow. We pass them through so the OfflineProvider + each
    // page's own offline UI can react appropriately.
    if (!error.response) return Promise.reject(error);

    const original = error.config || {};
    const url = original.url || '';

    // Skip our own auth endpoints — login/register failures are credential
    // errors (correctly surfaced to the user), and we must never refresh
    // on the refresh endpoint itself (infinite loop).
    const isAuthEndpoint =
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/logout') ||
      url.includes('/auth/verify-email');

    if (
      error.response.status === 401 &&
      !isAuthEndpoint &&
      !original._retried
    ) {
      original._retried = true;
      try {
        await __performGlobalRefresh();
        // Retry the original request — REQUEST interceptor will attach
        // the newly-stored Bearer token from localStorage.
        return axios(original);
      } catch (_refreshErr) {
        // Refresh genuinely failed (refresh_token expired/invalid). Clear
        // local tokens so the next /auth/me check at app boot is clean.
        window.__hlAuth?.clearTokens?.();
      }
    }

    return Promise.reject(error);
  },
);

// Check version before rendering app
console.log('🚀 HealthLoop Starting...');
const versionInfo = checkVersion();

if (versionInfo.changed) {
  console.log('✨ Version updated successfully');
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register service worker for offline support and auto-updates
serviceWorkerRegistration.register();

// Log app version for debugging
console.log('📦 App Version:', serviceWorkerRegistration.getAppVersion());
console.log('💡 Debug: Type window.HealthLoopDebug.help() for cache management commands');
